import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "components/ui/pagination";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useState } from "react";
import { ArticleCard } from "./article-card";
import { useFetchArticles } from "./fetch-hooks";
import { Skeleton } from "components/ui/skeleton";

interface ArticlesListProps {
  limit?: number; // If provided, only show this many articles (no pagination)
  onArticleClick?: (identifier: string) => void;
}

export const ArticlesList = ({ limit, onArticleClick }: ArticlesListProps) => {
  const [page, setPage] = useState(1);
  const pageSize = limit || 25;

  const articlesQuery = useFetchArticles(page, pageSize);

  const isLoading = articlesQuery.isPending;
  const isSuccess = articlesQuery.isSuccess;

  return (
    <div className="flex flex-col gap-4 w-full">
      <ReactQueryErrorWrapper queries={[articlesQuery]} mutations={[]} />

      {/* Loading Skeletons */}
      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: limit || 3 }).map((_, i) => (
            <Skeleton key={i} className="h-75 rounded-lg" />
          ))}
        </div>
      )}

      {/* Articles */}
      {isSuccess && (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {articlesQuery.data.data.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                onClick={
                  onArticleClick
                    ? () => onArticleClick(article.slug || article.documentId)
                    : undefined
                }
              />
            ))}
          </div>

          {/* Pagination - only show if not limited */}
          {!limit && articlesQuery.data.meta.pagination.pageCount > 1 && (
            <Pagination className="mt-6">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className={
                      page <= 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="text-sm text-muted-foreground px-4">
                    Page {page} of{" "}
                    {articlesQuery.data.meta.pagination.pageCount}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    onClick={() => {
                      setPage((p) =>
                        Math.min(
                          articlesQuery.data.meta.pagination.pageCount,
                          p + 1,
                        ),
                      );
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className={
                      page >= articlesQuery.data.meta.pagination.pageCount
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </div>
  );
};
